import { useEffect, useState } from "react";

import { Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "src/components/ui/button";
import { FormItem } from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { cn } from "src/lib/utils";

const MASK_CHAR = "•";

export function DescriptionSection({
  store,
  updateIntegration,
  loading,
  isAdmin,
  sectionTarget,
  highlightedSection,
}) {
  const [descriptionValue, setDescriptionValue] = useState(
    store?.description || ""
  );
  const { privacyMode } = useAdminPrivacyMode();
  const [revealStoreName, setRevealStoreName] = useState(!privacyMode);

  useEffect(() => {
    setDescriptionValue(store?.description || "");
  }, [store]);

  useEffect(() => {
    setRevealStoreName(!privacyMode);
  }, [privacyMode]);

  const isObfuscated = privacyMode && !revealStoreName;

  const [displayValue, setDisplayValue] = useState(descriptionValue);

  useEffect(() => {
    // whenever real value changes, refresh displayed value
    setDisplayValue(
      isObfuscated
        ? MASK_CHAR.repeat(descriptionValue.length)
        : descriptionValue
    );
  }, [descriptionValue, isObfuscated]);

  const handleDescriptionChange = async () => {
    const newVal = descriptionValue.trim();
    setDescriptionValue(newVal);

    if (!newVal) {
      setDescriptionValue(store?.description || "");
      return;
    }
    if (newVal === store?.description) return;

    await updateIntegration({
      id: store.id,
      input: {
        description: newVal,
      },
    });
  };

  return (
    <FormItem>
      <Label htmlFor="store-name">Store Name</Label>
      <div
        data-settings-section="store_name_input"
        className={cn(
          "rounded-xl transition-[background-color,box-shadow] duration-700",
          sectionTarget === "store_name_input" &&
            highlightedSection === "store_name_input" &&
            "bg-primary/5 ring-primary/30 ring-2"
        )}
      >
        <div className="relative">
          <Input
            id="store-name"
            value={isObfuscated ? displayValue : descriptionValue}
            onChange={(e) => {
              const nextDisplayed = e.target.value;

              if (!isObfuscated) {
                setDescriptionValue(nextDisplayed);
                return;
              }
              const nextLength = nextDisplayed.length;
              const previousLength = descriptionValue.length;

              if (nextLength < previousLength) {
                setDescriptionValue((prev) => prev.slice(0, nextLength));
              } else if (nextLength > previousLength) {
                setDescriptionValue(
                  (prev) => prev + "x".repeat(nextLength - previousLength)
                );
              }
              setDisplayValue(MASK_CHAR.repeat(nextLength));
            }}
            onBlur={handleDescriptionChange}
            className="flex-1 pr-10"
            disabled={loading || !isAdmin}
            type="text"
            autoComplete="off"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
          />

          <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
            {privacyMode && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setRevealStoreName((prev) => !prev)}
                aria-label={
                  revealStoreName ? "Hide store name" : "Show store name"
                }
              >
                {revealStoreName ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            )}
            {loading && <Loader2 className="size-4 animate-spin" />}
          </div>
        </div>
      </div>
    </FormItem>
  );
}
